import {Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {DateInput} from 'app/tfi/components/DateInput'
import {FormikErrors, FormikTouched} from 'formik'
import {useState} from 'react'
import {TFormAllValues, TFormExtraPolicy} from '../../../policies/policiesTypes'
import {isLifeKind, TPolicyFormKind} from '../../../../helpers/policyFormKind'

type TPolicyExtraFields =
  | 'hasAutoPolicy'
  | 'hasHomePolicy'
  | 'hasCarrierAccount'
  | 'accountUsername'
  | 'accountPassword'
  | 'accountCreatedByClient'

interface IExtraTab {
  onPrevStep: () => void
  handleSubmit: () => void
  handleChange: (field: string, value: string) => void
  handleBlur: (field: string) => void
  allValues: TFormExtraPolicy
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  policyValues: Pick<TFormAllValues, TPolicyExtraFields>
  onPolicyChange: (field: TPolicyExtraFields, value: boolean | string | null) => void
  isDisableEdit?: boolean
  formKind?: TPolicyFormKind
}

export const ExtraTab: React.FC<IExtraTab> = ({
  onPrevStep,
  handleSubmit,
  handleBlur,
  handleChange,
  allValues,
  errors,
  touched,
  policyValues,
  onPolicyChange,
  isDisableEdit = false,
  formKind = 'GENERIC',
}) => {
  const intl = useIntl()
  // En Vida, el médico primario y los medicamentos viven en la pestaña Medical
  // (estructurados en Policy_life_detail / Policy_life_medication). Mostrarlos también
  // acá crearía dos fuentes de verdad para el mismo dato.
  const isLife = isLifeKind(formKind)
  const selectPlaceholder = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })
  const [showPassword, setShowPassword] = useState(false)

  const toSelectValue = (value: boolean | null | undefined) => {
    if (value === true) return 'true'
    if (value === false) return 'false'
    return ''
  }

  const handleNullableBoolean = (field: 'hasAutoPolicy' | 'hasHomePolicy', value: string) => {
    const parsed = value === '' ? null : value === 'true'
    onPolicyChange(field, parsed)
  }

  return (
    <div className='policy-extratabs-container'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        <FormattedMessage
          id='policies.detail.form.extra.heading'
          defaultMessage='Add relevant information and submit the policy for revision'
        />
      </div>
      <Row className='mb-3'>
        <Col md={4} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='hasAutoPolicy' style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.detail.form.extra.hasAutoPolicy'
                defaultMessage='Has auto policy'
              />
            </Form.Label>
            <Form.Select
              id='hasAutoPolicy'
              name='hasAutoPolicy'
              value={toSelectValue(policyValues.hasAutoPolicy)}
              onChange={(e) => handleNullableBoolean('hasAutoPolicy', e.target.value)}
            >
              <option value=''>{selectPlaceholder}</option>
              <option value='true'>{intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}</option>
              <option value='false'>{intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='hasHomePolicy' style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.detail.form.extra.hasHomePolicy'
                defaultMessage='Has home policy'
              />
            </Form.Label>
            <Form.Select
              id='hasHomePolicy'
              name='hasHomePolicy'
              value={toSelectValue(policyValues.hasHomePolicy)}
              onChange={(e) => handleNullableBoolean('hasHomePolicy', e.target.value)}
            >
              <option value=''>{selectPlaceholder}</option>
              <option value='true'>{intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}</option>
              <option value='false'>{intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='hasCarrierAccount' style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.detail.form.extra.hasCarrierAccount'
                defaultMessage='Has Carrier Account'
              />
            </Form.Label>
            <Form.Select
              id='hasCarrierAccount'
              name='hasCarrierAccount'
              value={policyValues.hasCarrierAccount ? 'true' : 'false'}
              onChange={(e) => onPolicyChange('hasCarrierAccount', e.target.value === 'true')}
            >
              <option value='false'>{intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}</option>
              <option value='true'>{intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      {policyValues.hasCarrierAccount && (
        <Row className='mb-3'>
          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label htmlFor='accountUsername' style={{fontSize: '12px'}}>
                <FormattedMessage
                  id='policies.detail.form.extra.accountUsername'
                  defaultMessage='Account username'
                /> 
              </Form.Label>
              <Form.Control
                name='accountUsername'
                id='accountUsername'
                value={policyValues.accountUsername ?? ''}
                onChange={(e) => onPolicyChange('accountUsername', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label htmlFor='accountPassword' style={{fontSize: '12px'}}>
                <FormattedMessage
                  id='policies.detail.form.extra.accountPassword'
                  defaultMessage='Account password'
                />
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='new-password'
                  name='accountPassword'
                  id='accountPassword'
                  value={policyValues.accountPassword ?? ''}
                  onChange={(e) => onPolicyChange('accountPassword', e.target.value)}
                />
                <Button
                  variant='outline-secondary'
                  onClick={() => setShowPassword((prev) => !prev)}
                  className='border'
                  id='btn-account-password'
                >
                  {showPassword ? (
                    <i className='bi bi-eye-slash-fill' />
                  ) : (
                    <i className='bi bi-eye-fill' />
                  )}
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={12} sm={12} className='mt-2'>
            <Form.Check
              type='checkbox'
              id='accountCreatedByClient'
              label={
                <FormattedMessage
                  id='policies.detail.form.extra.accountCreatedByClient'
                  defaultMessage='Account created by client'
                />
              }
              checked={!!policyValues.accountCreatedByClient}
              onChange={(e) => onPolicyChange('accountCreatedByClient', e.target.checked)}
            />
          </Col>
        </Row>
      )}
      {/* Médico primario y medicamentos: en Vida los reemplaza la pestaña Medical. */}
      <Row className={isLife ? 'd-none' : undefined}>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='primaryDoctorInfo' style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.detail.form.extra.primaryDoctor'
                defaultMessage='Primary Doctor information'
              />
            </Form.Label>
            <Form.Control
              placeholder={intl.formatMessage({
                id: 'policies.detail.form.extra.primaryDoctorPlaceholder',
                defaultMessage: 'Type here the Primary Doctor information',
              })}
              as='textarea'
              rows={3}
              name='primaryDoctorInfo'
              id='primaryDoctorInfo'
              value={allValues.primaryDoctorInfo}
              onChange={(e) => handleChange('primaryDoctorInfo', e.target.value)}
              onBlur={() => handleBlur('primaryDoctorInfo')}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='medicinesNeeded' style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.detail.form.extra.medicines'
                defaultMessage='Medicines needed'
              />
            </Form.Label>
            <Form.Control
              placeholder={intl.formatMessage({
                id: 'policies.detail.form.extra.medicinesPlaceholder',
                defaultMessage: 'List the medicines the client requested to be covered',
              })}
              as='textarea'
              rows={3}
              name='medicinesNeeded'
              id='medicinesNeeded'
              value={allValues.medicinesNeeded}
              onChange={(e) => handleChange('medicinesNeeded', e.target.value)}
              onBlur={() => handleBlur('medicinesNeeded')}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='infoExtra' style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.detail.form.extra.notes'
                defaultMessage='Extra notes or comments'
              />
            </Form.Label>
            <Form.Control
              placeholder={intl.formatMessage({
                id: 'policies.detail.form.extra.notesPlaceholder',
                defaultMessage: 'Type here relevant information or comments about this policy...',
              })}
              as='textarea'
              rows={5}
              name='infoExtra'
              id='infoExtra'
              value={allValues.infoExtra}
              onChange={(e) => handleChange('infoExtra', e.target.value)}
              onBlur={() => handleBlur('infoExtra')}
            />
            <small className='text-primary fw-bold'>
              <span style={{textDecoration: 'underline'}}>
                <FormattedMessage
                  id='policies.detail.form.extra.importantLabel'
                  defaultMessage='IMPORTANT:'
                />
              </span> <FormattedMessage
                id='policies.detail.form.extra.clientVisibleNote'
                defaultMessage='The client can view the information provided in this field.'
              />
            </small>
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <DateInput
            id="cancellationDate"
            name="cancellationDate"
            label={intl.formatMessage({
              id: 'policies.detail.form.extra.cancellationDate',
              defaultMessage: 'Cancellation date',
            })}
            value={allValues.cancellationDate}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {/* El Special Enrollment Period es un mecanismo exclusivo del Marketplace. */}
          {!isLife && (
            <>
              <DateInput
                id="specialEnrollmentDate"
                name="specialEnrollmentDate"
                label={intl.formatMessage({
                  id: 'policies.detail.form.extra.specialEnrollmentDate',
                  defaultMessage: 'Special enrollment date',
                })}
                value={allValues.specialEnrollmentDate}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Form.Group>
                <Form.Label htmlFor='specialEnrollmentReason' style={{fontSize: '12px'}}>
                  <FormattedMessage
                    id='policies.detail.form.extra.specialEnrollmentReason'
                    defaultMessage='Special enrollment reason'
                  />
                </Form.Label>
                <Form.Control
                  placeholder={intl.formatMessage({
                    id: 'policies.detail.form.extra.specialEnrollmentReasonPlaceholder',
                    defaultMessage: 'Special enrollment reason...',
                  })}
                  as='textarea'
                  rows={2}
                  name='specialEnrollmentReason'
                  id='specialEnrollmentReason'
                  value={allValues.specialEnrollmentReason}
                  onChange={(e) => handleChange('specialEnrollmentReason', e.target.value)}
                  onBlur={() => handleBlur('specialEnrollmentReason')}
                />
              </Form.Group>
            </>
          )}
        </Col>
      </Row>
      <div className='btn-prev-next'>
        <Button size='sm' variant='secondary' onClick={onPrevStep}>
          <FormattedMessage id='policies.list.wizard.previous' defaultMessage='PREVIOUS' />
        </Button>
        <Button size='sm' variant='info' onClick={handleSubmit}>
          <i className='bi bi-save fs-3' /> <FormattedMessage id='common.action.save' defaultMessage='Save' />
        </Button>
      </div>
    </div>
  )
}
